import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import { ApiLoadingState } from '../../../middleware/api/apiList'
import { RootState } from '../../../stateStore'
import { LoadingState, ValidationError } from '../../../types/commonTypes'
import FormSixteenUploadApi from '../../../middleware/api/Finance/FormSixteenUpload/FormSixteenUploadApi'
import {
  FormSixteenUploadState,
  getForm16FilesProps,
  getForm16FilesResponse,
  uploadFilesProps,
} from '../../../types/Finance/FormSixteenUpload/FormSixteenUploadTypes'

const getUploadForm16 = createAsyncThunk(
  'formSixteenUpload/getUploadForm16',
  async (data: uploadFilesProps, thunkApi) => {
    try {
      return await FormSixteenUploadApi.getUploadForm16(data)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getForm16Files = createAsyncThunk(
  'formSixteenUpload/getForm16Files',
  async (data: getForm16FilesProps, thunkApi) => {
    try {
      return await FormSixteenUploadApi.getForm16Files(data)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const deleteMultipleCheckedForm16Files = createAsyncThunk(
  'formSixteenUpload/deleteMultipleInsuranceDetails',
  async (props: number[], thunkApi) => {
    try {
      return await FormSixteenUploadApi.deleteMultipleCheckedForm16Files(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

export const initialFormSixteenUploadState: FormSixteenUploadState = {
  isLoading: ApiLoadingState.idle,
  getForm16FilesList: [],
  listSize: 0,
  getForm16FilesResponse: {} as getForm16FilesResponse,
  isLoadingUpload: false,
  checkedId: [],
  isDelete: false,
}

const formSixteenUploadSlice = createSlice({
  name: 'formSixteenUpload',
  initialState: initialFormSixteenUploadState,
  reducers: {
    setIsLoadingUpload: (state, action) => {
      state.isLoadingUpload = action.payload
    },
    setCheckedId: (state, action) => {
      state.checkedId = action.payload
    },
    setIsDelete: (state, action) => {
      state.isDelete = action.payload
    },
  },
  extraReducers(builder) {
    builder

      .addCase(getForm16Files.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.getForm16FilesResponse = action.payload
        state.listSize = action.payload.size
      })
      .addCase(getForm16Files.pending, (state) => {
        state.isLoading = ApiLoadingState.loading
      })
      .addCase(getForm16Files.rejected, (state) => {
        state.isLoading = ApiLoadingState.failed
      })
      .addCase(getUploadForm16.fulfilled, (state) => {
        state.isLoading = ApiLoadingState.succeeded
      })
      .addCase(getUploadForm16.pending, (state) => {
        state.isLoading = ApiLoadingState.loading
      })
      .addCase(getUploadForm16.rejected, (state) => {
        state.isLoading = ApiLoadingState.failed
      })
  },
})

const isLoading = (state: RootState): LoadingState =>
  state.formSixteenUpload.isLoading

const getFormSixteenResponse = (state: RootState): getForm16FilesResponse =>
  state.formSixteenUpload.getForm16FilesResponse

const listSize = (state: RootState): number => state.formSixteenUpload.listSize

const isLoadingUpload = (state: RootState): boolean =>
  state.formSixteenUpload.isLoadingUpload

const getCheckId = (state: RootState): number[] =>
  state.formSixteenUpload.checkedId

const getIsDelete = (state: RootState): boolean =>
  state.formSixteenUpload.isDelete

const formSixteenUploadThunk = {
  getUploadForm16,
  getForm16Files,
  deleteMultipleCheckedForm16Files,
}

const formSixteenUploadSelectors = {
  isLoading,
  getFormSixteenResponse,
  listSize,
  isLoadingUpload,
  getCheckId,
  getIsDelete,
}

export const formSixteenUploadService = {
  ...formSixteenUploadThunk,
  actions: formSixteenUploadSlice.actions,
  selectors: formSixteenUploadSelectors,
}

export default formSixteenUploadSlice.reducer
